import { isAnyStorageKey, isStorageKey } from 'projects/wo-common/src/lib/ngrx/storage';
import { tuiAvatarOptionsProvider } from '@taiga-ui/kit';
import { TUI_TEXTFIELD_APPEARANCE_DIRECTIVE, TuiDialogModule, TuiRootModule } from '@taiga-ui/core';
import { TUI_DATE_FORMAT, TUI_DATE_SEPARATOR } from '@taiga-ui/cdk';
import { TuiMobileCalendarModule } from '@taiga-ui/addon-mobile';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { Store, StoreModule } from '@ngrx/store';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { EffectsModule } from '@ngrx/effects';
import { provideDialogConfig } from '@ngneat/dialog';
import { UserContextModule, WO_USER_CONTEXT } from '@iss/libs/user-context';
import { GraphQLModule } from '@iss/libs/graphql';
import { AuthModule, WO_PROTECTED_RESOURCES, WO_USER_PERMISSIONS, WO_USER_ROLES } from '@iss/libs/auth';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { bootstrapApplication } from '@angular/platform-browser';
import { enableProdMode, importProvidersFrom, LOCALE_ID } from '@angular/core';
import localeDk from '@angular/common/locales/da';
import { HTTP_INTERCEPTORS, HttpBackend, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { DATE_PIPE_DEFAULT_OPTIONS, DatePipeConfig, registerLocaleData } from '@angular/common';

import assetsMap from './assets.json';
import { AppRoutingModule, AppRoutingPaths } from './app/routing';
import { AppComponent } from './app/app.component';
import {
  appContextFeature,
  AppContextStateModule,
  authFeature,
  effects,
  metaReducers,
  reducers,
  userContextSelectors,
} from './app/+state';

import { environment } from '@wo-environments';
import { MultiTranslateHttpLoader } from '@iss/wo-common/utils';
import { IssMissingTranslationHandler } from '@iss/wo-common/ngx-translate';
import { CONSENT_APPROVAL_PATH, ConsentApprovalErrorInterceptor } from '@iss/wo-common/modules/consent-approval';
import { provideLocalStorageService } from '@iss/wo-common/local-storage';
import { FileHashPipe, hashFilesProvider } from '@iss/wo-common/hash-files';
import { ImpersonateModule } from '@iss/wo-common/components';
import { BreadcrumbsModule, PageTitleBarModule, SideNavModule, ToastModule } from '@iss/core/portal';
import { ErrorStateMatcher } from '@iss/core/forms';
import { DRAWER_PROVIDER } from '@iss/core/drawer';

registerLocaleData(localeDk);

if (environment.production) {
  enableProdMode();
}

// AoT requires an exported function for factories
export function HttpLoaderFactory(httpBackend: HttpBackend, fileHashPipe: FileHashPipe) {
  return new MultiTranslateHttpLoader(httpBackend, fileHashPipe, [
    'assets/i18n/',
    'core/assets/i18n/',
    'wo-common/assets/i18n/',
  ]);
}

const isVolatileValue = (key: string): boolean => isAnyStorageKey(key)
  ? !isStorageKey(key, appContextFeature.name) && !isStorageKey(key, authFeature.name)
  : false;

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      AuthModule,
      AppRoutingModule,
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [ HttpBackend, FileHashPipe ],
        },
      }),
      StoreModule.forRoot(reducers, {
        metaReducers,
      }),
      AppContextStateModule,
      StoreRouterConnectingModule.forRoot(),
      StoreDevtoolsModule.instrument({
        maxAge: 25,
        logOnly: environment.production,
        autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      }),
      EffectsModule.forRoot(effects),
      GraphQLModule,
      SideNavModule.forRoot(),
      BreadcrumbsModule.forRoot(),
      PageTitleBarModule.forRoot(),
      BrowserAnimationsModule,
      TuiRootModule,
      TuiDialogModule,
      TuiMobileCalendarModule,
      ToastModule,
      ImpersonateModule,
      UserContextModule,
    ),
    {
      provide: CONSENT_APPROVAL_PATH,
      useValue: [ '/', AppRoutingPaths.CONSENT_APPROVAL ],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ConsentApprovalErrorInterceptor,
      multi: true,
    },
    FileHashPipe,
    {
      provide: LOCALE_ID,
      useValue: 'en-GB',
    },
    {
      provide: MissingTranslationHandler,
      useClass: IssMissingTranslationHandler,
    },
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: {
        dateFormat: 'dd/MM/yyyy',
      } as DatePipeConfig,
    },
    {
      provide: TUI_DATE_FORMAT,
      useValue: 'DMY',
    },
    {
      provide: TUI_DATE_SEPARATOR,
      useValue: '/',
    },
    {
      provide: TUI_TEXTFIELD_APPEARANCE_DIRECTIVE,
      useValue: {
        appearance: 'iss-textfield',
      },
    },
    tuiAvatarOptionsProvider({
      size: 'l',
      autoColor: true,
      rounded: true,
    }),
    {
      provide: WO_PROTECTED_RESOURCES,
      useValue: [ '/graphql?auth', '/user', '/schedule/upload', '/upload', '/rollback', '/actuator/info' ],
    },
    {
      provide: WO_USER_ROLES,
      useFactory: (store: Store) => store.select(appContextFeature.selectRoles),
      deps: [ Store ],
    },
    {
      provide: WO_USER_PERMISSIONS,
      useFactory: (store: Store) => store.select(appContextFeature.selectPermissions),
      deps: [ Store ],
    },
    {
      provide: WO_USER_CONTEXT,
      useFactory: (store: Store) => store.select(userContextSelectors.selectUserContext),
      deps: [ Store ],
    },
    ErrorStateMatcher,
    provideDialogConfig({
      enableClose: false,
    }),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    DRAWER_PROVIDER,
    hashFilesProvider(assetsMap),
    ...provideLocalStorageService(isVolatileValue),
  ],
})
  .catch((err) => console.error(err));
